import React, { useCallback, useEffect, useState } from "react";
import { ShoppingBag, Store, Store as StoreIcon, Search, Inbox, Heart, Clock, ShoppingCart, Package } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { getRecentlyViewed } from "@/lib/recent";
import { fileUrl } from "@/lib/uploads";
import { formatPrice } from "@/lib/currency";
import ProductCard from "@/components/market/ProductCard";
import ProductDetail from "@/components/market/ProductDetail";
import ShopPanel from "@/components/market/ShopPanel";
import MarketInbox from "@/components/market/MarketInbox";
import MarketThread from "@/components/market/MarketThread";
import FeaturedShops from "@/components/market/FeaturedShops";
import Storefront from "@/components/market/Storefront";
import CartView from "@/components/market/CartView";
import OrdersView from "@/components/market/OrdersView";
import LiveBar from "@/components/live/LiveBar";
import HelpGuide, { HelpButton } from "@/components/shared/HelpGuide";
import { toast } from "sonner";

export default function MarketplacePage({ socialMsg, initialView = "browse", openProductId, onProductOpened }) {
  const { t } = useI18n();
  const [view, setView] = useState(initialView); // browse | saved | shop | inbox
  const [guide, setGuide] = useState(false);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [thread, setThread] = useState(null); // {peer, productId}
  const [detail, setDetail] = useState(null); // productId
  const [storefront, setStorefront] = useState(null); // shopId
  const [inboxKey, setInboxKey] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [wishlist, setWishlist] = useState([]);
  const [recent, setRecent] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => { api.get("/market/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/market/cart").then(({ data }) => setCartCount(data.count)).catch(() => {}); }, []);
  const addToCart = async (product, variantKey, qty = 1) => {
    try { const { data } = await api.post("/market/cart/add", { product_id: product.id, variant_key: variantKey, qty }); setCartCount(data.count); toast.success(t("m_added_to_cart")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("m_could_not_save")); }
  };
  const loadWishlist = useCallback(() => { api.get("/market/wishlist").then(({ data }) => setWishlist(data)).catch(() => {}); }, []);
  const loadProducts = useCallback((query = "", cat = "") => {
    const params = {}; if (query) params.q = query; if (cat) params.category = cat;
    api.get("/market/products", { params }).then(({ data }) => setProducts(data)).catch(() => {});
  }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { if (view === "browse") { const t = setTimeout(() => loadProducts(q, category), 300); return () => clearTimeout(t); } }, [q, category, view, loadProducts]);
  useEffect(() => { if (view === "browse" && !detail && !storefront) setRecent(getRecentlyViewed()); }, [view, detail, storefront]);
  useEffect(() => { api.get("/market/inbox").then(({ data }) => setInboxUnread(data.reduce((s, c) => s + (c.unread || 0), 0))).catch(() => {}); }, [inboxKey, socialMsg]);
  // Open a deep-linked / shared product
  useEffect(() => {
    if (openProductId) { setThread(null); setStorefront(null); setView("browse"); setDetail(openProductId); onProductOpened && onProductOpened(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProductId]);

  const currency = settings?.currency || "USD";
  const messageSeller = (product) => { setDetail(null); setStorefront(null); setThread({ peer: { id: product.owner_id, name: product.shop_name || product.seller?.name || t("m_seller") }, productId: product.id }); };
  const gridCls = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3";

  if (thread) {
    return (
      <div className="h-full bg-[var(--rc-bg)] px-4 pt-4 rc-safe-top" data-testid="marketplace-page">
        <MarketThread peer={thread.peer} productId={thread.productId} socketMessage={socialMsg}
          onBack={() => { setThread(null); setInboxKey((k) => k + 1); }} />
      </div>
    );
  }
  if (detail) {
    return (
      <div className="h-full overflow-y-auto no-scrollbar bg-[var(--rc-bg)] px-4 pt-4 rc-safe-top" data-testid="marketplace-page">
        <div className="max-w-3xl mx-auto">
          <ProductDetail productId={detail} currency={currency} onBack={() => setDetail(null)} onMessage={messageSeller} onAddToCart={addToCart} />
        </div>
      </div>
    );
  }
  if (storefront) {
    return (
      <div className="h-full overflow-y-auto no-scrollbar bg-[var(--rc-bg)] px-4 pt-4 rc-safe-top" data-testid="marketplace-page">
        <div className="max-w-5xl mx-auto">
          <Storefront shopId={storefront} currency={currency} onBack={() => setStorefront(null)} onOpenProduct={(pid) => setDetail(pid)} onMessage={messageSeller} />
        </div>
      </div>
    );
  }

  const tabs = [["browse", ShoppingBag, t("m_browse")], ["saved", Heart, t("m_saved")], ["orders", Package, t("m_orders")], ["shop", Store, t("m_my_shop")], ["inbox", Inbox, t("m_inbox")]];

  return (
    <div className="h-full overflow-y-auto no-scrollbar bg-[var(--rc-bg)]" data-testid="marketplace-page">
      <div className="rc-safe-top px-4 pt-6 pb-24 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-heading text-5xl px-1">{t("m_market")}</h1>
          <div className="flex items-center gap-2">
            <HelpButton onClick={() => setGuide(true)} testid="market-help-btn" />
            <button onClick={() => setView("cart")} data-testid="market-cart-btn" className="relative h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] text-[var(--rc-gold)]">
              <ShoppingCart size={20} />
              {cartCount > 0 && <span data-testid="cart-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-[11px] font-bold">{cartCount}</span>}
            </button>
          </div>
        </div>
        <div className="flex rounded-full bg-[var(--rc-surface)] p-1 mb-5 max-w-xl">
          {tabs.map(([k, Icon, l]) => (
            <button key={k} onClick={() => { setView(k); if (k === "saved") loadWishlist(); }} data-testid={`market-nav-${k}`}
              className={`relative flex-1 flex items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition-colors ${view === k ? "bg-[var(--rc-gold)]/20 text-[var(--rc-gold)]" : "text-[var(--rc-muted)]"}`}>
              <Icon size={16} /> <span>{l}</span>
              {k === "inbox" && inboxUnread > 0 && <span data-testid="market-inbox-badge" className="absolute top-1 right-2 h-4 min-w-4 px-1 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-[10px] font-bold">{inboxUnread > 9 ? "9+" : inboxUnread}</span>}
            </button>
          ))}
        </div>

        {view === "browse" && (
          <>
            {!q && !category && <LiveBar module="market" showGoLive={false} />}
            <div className="flex items-center gap-2 rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] px-4 mb-4 max-w-2xl">
              <Search size={17} className="text-[var(--rc-muted)]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("m_search_items")} data-testid="market-search" className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none" />
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 -mx-1 px-1" data-testid="category-chips">
              {["", ...(settings?.categories || [])].map((c) => (
                <button key={c || "all"} onClick={() => setCategory(c)} data-testid={`category-${c || "all"}`}
                  className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${category === c ? "bg-[var(--rc-gold)] text-black border-[var(--rc-gold)]" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>
                  {c || t("m_all")}
                </button>
              ))}
            </div>

            {/* Featured shops (item 3) */}
            {!q && !category && <FeaturedShops onOpenShop={(id) => setStorefront(id)} />}

            {/* Recently viewed (item 7) */}
            {!q && !category && recent.length > 0 && (
              <div className="mb-5" data-testid="recently-viewed">
                <div className="flex items-center gap-1.5 mb-2 text-sm font-medium"><Clock size={15} className="text-[var(--rc-gold)]" /> {t("m_recently_viewed")}</div>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                  {recent.map((it) => (
                    <button key={it.id} onClick={() => setDetail(it.id)} data-testid={`recent-item-${it.id}`} className="w-28 shrink-0 text-left">
                      <div className="aspect-square rounded-xl overflow-hidden bg-[var(--rc-bg)] border border-[var(--rc-border)]">
                        {it.image ? <img src={fileUrl(it.image)} alt="" className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-2xl">🛍️</div>}
                      </div>
                      <div className="text-xs mt-1 truncate">{it.title}</div>
                      {it.price != null && <div className="text-xs text-[var(--rc-gold)] font-semibold">{formatPrice(it.price, it.currency || currency)}</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {products.length ? (
              <div className={gridCls}>
                {products.map((p) => <ProductCard key={p.id} product={p} currency={currency} onMessage={messageSeller} onOpen={(prod) => setDetail(prod.id)} onAddToCart={(prod) => (prod.variants && prod.variants.length) ? setDetail(prod.id) : addToCart(prod)} onToggleSave={(id, saved) => setProducts((ps) => ps.map((x) => x.id === id ? { ...x, saved } : x))} />)}
              </div>
            ) : <div className="text-center py-16 text-[var(--rc-muted)]"><StoreIcon size={38} className="mx-auto mb-3 text-[var(--rc-gold)]" />{t("m_no_items")}</div>}
          </>
        )}

        {view === "saved" && (
          wishlist.length ? (
            <div className={gridCls}>
              {wishlist.map((p) => <ProductCard key={p.id} product={p} currency={currency} onMessage={messageSeller} onOpen={(prod) => setDetail(prod.id)} onAddToCart={(prod) => (prod.variants && prod.variants.length) ? setDetail(prod.id) : addToCart(prod)} onToggleSave={(id, saved) => { if (!saved) setWishlist((w) => w.filter((x) => x.id !== id)); }} />)}
            </div>
          ) : <div className="text-center py-16 text-[var(--rc-muted)]" data-testid="wishlist-empty"><Heart size={38} className="mx-auto mb-3 text-[var(--rc-gold)]" />{t("m_no_saved")}</div>
        )}

        {view === "orders" && <OrdersView />}
        {view === "shop" && settings && <ShopPanel settings={settings} />}
        {view === "inbox" && <MarketInbox onOpenThread={(peer) => setThread({ peer, productId: null })} refreshKey={inboxKey + (socialMsg ? 1 : 0)} />}
        {view === "cart" && <CartView onChanged={setCartCount} />}
      </div>
      {guide && (
        <HelpGuide testid="market-guide-overlay" title={t("guide_market_title")}
          steps={[t("guide_market_1"), t("guide_market_2"), t("guide_market_3"), t("guide_market_4")]}
          onClose={() => setGuide(false)} />
      )}
    </div>
  );
}
